import React from 'react';
import Box from 'sail/Box';
import Select from 'sail/Select';
import {BodyAlt} from 'sail/Text';
import ContentTabs, {ContentTab} from 'sail/ContentTabs';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useGuide} from '../../data/GuideProvider';
import ColorPicker from './ColorPicker';
import {FormattedMessageOrString} from '../../../../data/localization/intlUtils';

const FONT_FAMILES = [
  {
    value: 'Arial, sans-serif',
    label: 'Sans-serif',
  },
  {
    value: 'Times, serif',
    label: 'Serif',
  },
  {
    value: 'Courier, monospace',
    label: 'Monospace',
  },
  {
    value: 'Roboto, sans-serif',
    label: 'Roboto, sans-serif',
  },
];

const messages = defineMessages({
  fontFamily: {
    id: 'docsApp.integrationBuilder.elementsEditor.fontFamily',
    description: 'Label for font family selector',
    defaultMessage: 'Font family',
  },
  font: {
    id: 'docsApp.integrationBuilder.elementsEditor.font',
    description: 'Label for font selector',
    defaultMessage: 'Font',
  },
  textColor: {
    id: 'docsApp.integrationBuilder.elementsEditor.textColor',
    description: 'Label for text color selector',
    defaultMessage: 'Text color',
  },
  background: {
    id: 'docsApp.integrationBuilder.elementsEditor.background',
    description: 'Label for background color selector',
    defaultMessage: 'Background',
  },
  invalidColor: {
    id: 'docsApp.integrationBuilder.elementsEditor.invalidColor',
    description: 'Label for what color to use for invalid things',
    defaultMessage: 'Invalid color',
  },
  editSections: {
    id: 'docsApp.integrationBuilder.elementsEditor.editSections',
    description: 'Label for editing sections of the form',
    defaultMessage: 'Edit sections of the payment form',
  },
});

// Highlights the updated value in <Code/>
const updateUI = (value) => () => {
  const els = document.querySelectorAll(
    `.token.animate[data-value="${value}"]`,
  );
  els.forEach((el) => {
    el.classList.add('edited');
    setTimeout(() => {
      el.classList.remove('edited');
    }, 2000);
  });
};

const InputOptions = () => {
  const {activeDemo, updateDemo, fireEvent} = useGuide();
  const intl = useIntl();

  if (!activeDemo) {
    return null;
  }
  const updateCode = (key, value) => {
    fireEvent('edited_element', {
      css_prop: key,
    });
    updateDemo({
      ...activeDemo,
      settings: {...activeDemo.settings, [key]: value},
    });
    setTimeout(updateUI(key), 200);
  };

  const demoSettings = activeDemo.settings;

  return (
    <Box
      flex={{direction: 'column', justifyContent: 'center'}}
      padding={{top: 8}}
    >
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.fontFamily} />
        </BodyAlt>
        <Select
          aria-label={intl.formatMessage(messages.fontFamily)}
          value={demoSettings.cardFont}
          options={FONT_FAMILES}
          width={172}
          onChange={(value) => {
            updateCode('cardFont', value);
          }}
        />
      </Box>
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.textColor} />
        </BodyAlt>
        <ColorPicker
          hex={demoSettings.cardText}
          onChange={(value) => {
            const color = value || '32325d';
            updateCode('cardText', color);
          }}
        />
      </Box>
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.invalidColor} />
        </BodyAlt>
        <ColorPicker
          hex={demoSettings.cardInvalid}
          onChange={(value) => {
            const color = value || '32325d';
            updateCode('cardInvalid', color);
          }}
        />
      </Box>
    </Box>
  );
};

const ButtonOptions = () => {
  const intl = useIntl();
  const {activeDemo, updateDemo, fireEvent} = useGuide();
  if (!activeDemo) {
    return null;
  }

  const updateCode = (key, value) => {
    fireEvent('edited_element', {
      css_prop: key,
    });
    updateDemo({
      ...activeDemo,
      settings: {...activeDemo.settings, [key]: value},
    });
    setTimeout(updateUI(key), 200);
  };

  const demoSettings = activeDemo.settings;

  return (
    <Box
      flex={{direction: 'column', justifyContent: 'center'}}
      padding={{top: 8}}
    >
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.fontFamily} />
        </BodyAlt>
        <Select
          aria-label={intl.formatMessage(messages.font)}
          value={demoSettings.buttonFont}
          options={FONT_FAMILES}
          width={172}
          onChange={(value) => {
            updateCode('buttonFont', value);
          }}
        />
      </Box>
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.textColor} />
        </BodyAlt>
        <ColorPicker
          hex={demoSettings.buttonColor}
          onChange={(value) => {
            const color = value || 'ffffff';
            updateCode('buttonColor', color);
          }}
        />
      </Box>
      <Box flex={{alignItems: 'center'}} margin={{top: 12}}>
        <BodyAlt style={{width: 100, marginRight: 16}}>
          <FormattedMessage {...messages.background} />
        </BodyAlt>
        <ColorPicker
          hex={demoSettings.buttonBg}
          onChange={(value) => {
            const color = value || '5469d4';
            updateCode('buttonBg', color);
          }}
        />
      </Box>
    </Box>
  );
};

export function ElementsEditor(props) {
  const {active, tabs, step} = props;
  const {setTab} = useGuide();
  const intl = useIntl();
  return (
    <Box
      flex={{direction: 'column'}}
      margin={{bottom: 8}}
      className="ElementsEditor"
    >
      <ContentTabs
        aria-label={intl.formatMessage(messages.editSections)}
        size="medium"
      >
        {Object.keys(tabs).map((key) => (
          <ContentTab
            key={key}
            onClick={(evt) => {
              evt.stopPropagation();
              setTab(step, key, tabs[key].file);
            }}
            active={active === tabs[key].type}
          >
            <FormattedMessageOrString stringOrDefineMessage={tabs[key].label} />
          </ContentTab>
        ))}
      </ContentTabs>
      {active === 'input' ? <InputOptions /> : <ButtonOptions />}
    </Box>
  );
}
